import { useMutation, useQuery } from "@tanstack/react-query"
import GameScreenLayout from "@/components/GameScreenLayout"
import { claimBase, getBaseConfig, getBaseMap, getMyBase } from "@/api/gameApi"
import { usePlayerGpsState } from "@/hooks/usePlayerGpsState"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function BaseMapPage() {
  const gps = usePlayerGpsState()
  const { syncGameState } = useGuardiansPassport()

  const configQuery = useQuery({
    queryKey: ["base-config"],
    queryFn: getBaseConfig,
  })

  const mineQuery = useQuery({
    queryKey: ["base-mine"],
    queryFn: getMyBase,
  })

  const mapQuery = useQuery({
    queryKey: ["base-map", gps?.lat, gps?.lng],
    queryFn: () => {
      if (!gps) throw new Error("GPS")
      const pad = 0.03
      return getBaseMap({
        plane: "SURFACE",
        bounds: {
          minLat: gps.lat - pad,
          minLng: gps.lng - pad,
          maxLat: gps.lat + pad,
          maxLng: gps.lng + pad,
        },
      })
    },
    enabled: Boolean(gps),
  })

  const claim = useMutation({
    mutationFn: () => {
      if (!gps) throw new Error("GPS")
      return claimBase({
        plane: "SURFACE",
        playerLat: gps.lat,
        playerLng: gps.lng,
        remote: true,
      })
    },
    onSuccess: async () => {
      await syncGameState()
      void mineQuery.refetch()
      void mapQuery.refetch()
    },
  })

  const mine = mineQuery.data
  const bases = mapQuery.data?.bases ?? []

  return (
    <GameScreenLayout title="Базы" backTo="/play/map">
      {!gps ? (
        <p className="text-sm text-amber-200">Нужна геолокация.</p>
      ) : null}

      {configQuery.data ? (
        <p className="mb-3 text-xs text-guardians-muted">
          Захват (remote): {configQuery.data.claim.CLAIM_REMOTE_COINS} 🪙 · радиус{" "}
          {configQuery.data.hex.claimRadiusMeters} м
        </p>
      ) : null}

      {mine?.base ? (
        <section className="mb-4 rounded-xl border border-guardians-accent/30 p-3 text-sm">
          <p className="font-medium">Ваша база</p>
          <p className="text-guardians-muted">
            Модули: ядро {mine.base.modules.core} · склад {mine.base.modules.warehouse}
          </p>
        </section>
      ) : mine?.canClaim ? (
        <button
          type="button"
          disabled={!gps || claim.isPending}
          onClick={() => claim.mutate()}
          className="mb-4 w-full rounded-xl bg-guardians-accent py-3 text-sm text-guardians-bg disabled:opacity-40"
        >
          Заложить базу здесь
        </button>
      ) : (
        <p className="mb-3 text-sm text-guardians-muted">Захват недоступен.</p>
      )}

      <h3 className="mb-2 text-sm font-medium">Базы рядом ({bases.length})</h3>
      <ul className="max-h-80 space-y-2 overflow-y-auto text-sm">
        {bases.map((b) => (
          <li
            key={b.id}
            className="flex justify-between rounded-lg border border-white/10 px-3 py-2"
          >
            <span>{b.nickname}</span>
            <span className="text-guardians-muted">Lv.{b.coreLevel}</span>
          </li>
        ))}
      </ul>

      {claim.isError ? (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(claim.error, "Захват не удался")}
        </p>
      ) : null}
    </GameScreenLayout>
  )
}
